import { useCallback, useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSetAtom, useStore } from 'jotai'
import * as Sentry from '@sentry/electron/renderer'
import { GlobalEvents } from '@highlight/ipc'

import { useAuth } from '@renderer/chat/providers'
import { useIsAgenticChatEnabled } from '@renderer/chat/hooks/use-agentic-chat'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { stagedActionEventProperties } from '@renderer/overlay/Sidepanel/hooks/staged-actions/track-staged-action'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'

import { ActionEditState, stagedActionEditStatesAtom } from '../../atoms'
import {
  archivedDisplayAtom,
  manuallyHiddenAtom,
  pendingDisplayAtom,
  undoErrorsAtom,
  undoingIdAtom,
  invokeErrorsAtom,
} from '../staged-action-local-state'
import { clearArchiveMarks, forgetArchiveState, rememberArchiveState } from './archive-marks'
import { releaseAnswerableRun } from './answerable-dismissal'
import { clearExecutionMarks, forgetExecution } from './execution-marks'
import { useStagedActionEndpoints } from './endpoints'
import { schedulePendingMetadata, useClearPendingDisplay, useMetadataFlush } from './metadata'
import { useVisibleStagedActions } from './visible-actions-context'
import { FOR_YOU_CHATS_QUERY_KEY, HISTORY_ACTIVITY_QUERY_KEY } from '@renderer/stores/conversation-query-keys'
import { chunkIds, isMockAction, markDeleted, normalizeUniqueIds, STAGED_ACTIONS_QUERY_KEY } from './utils'
import type { CreateStagedActionInput, GenerateStagedActionsInput, StagedAction } from './types'
import type { UndoErrorEntry } from '../staged-action-local-state'

const MAX_MANUALLY_HIDDEN_ACTIONS = 500

export function useStagedActionUndo() {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()
  const setUndoingId = useSetAtom(undoingIdAtom)
  const setUndoErrors = useSetAtom(undoErrorsAtom)
  const setManuallyHidden = useSetAtom(manuallyHiddenAtom)
  const clearPendingDisplay = useClearPendingDisplay()

  const setUndoError = useCallback(
    (id: string, entry: UndoErrorEntry) => {
      setUndoErrors((prev) => {
        const next = new Map(prev)
        next.set(id, entry)
        return next
      })
    },
    [setUndoErrors],
  )
  const clearUndoError = useCallback(
    (id: string) => {
      setUndoErrors((prev) => {
        if (!prev.has(id)) return prev
        const next = new Map(prev)
        next.delete(id)
        return next
      })
    },
    [setUndoErrors],
  )

  const undoMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = await getAuthToken()
      const response = await fetch(getChatApiRoute(`api/v1/staged-actions/${id}/undo`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Failed to undo staged action')
        let errorMessage = 'Failed to undo staged action'
        try {
          const parsed = JSON.parse(errorText)
          errorMessage =
            typeof parsed?.error === 'string' ? parsed.error : parsed?.error?.message || parsed?.message || errorMessage
        } catch {
          errorMessage = errorText || errorMessage
        }
        throw new Error(errorMessage)
      }
      return id
    },
    onMutate: async (id: string) => {
      setUndoingId(id)
      clearUndoError(id)
      clearPendingDisplay(id)
      // Taking the execution back is the one case where losing outputData is the
      // newer truth, not a stale read — drop the mark or the reconciler puts the
      // result straight back on the next fetch.
      forgetExecution(id)
      // The action may have already expired into manuallyHidden (its completed
      // window elapsed before the user undid it). Clear it so the reverted,
      // now-staged action re-surfaces in the list immediately instead of staying
      // hidden. Peers do the same off the staged-action:reverted signal below.
      setManuallyHidden((prev) => {
        if (!prev.has(id)) return prev
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      // pendingDisplay/undoErrors are window-local and shadow the synced cache, so
      // clearing them here only reverts *this* window's cell. Tell peer windows
      // (where this action may have been invoked, holding their own pending marker)
      // to drop theirs. emitToPeers is precise — it fires only on a real undo, so a
      // peer's stale full-list broadcast can never be mistaken for a revert.
      GlobalEvents.emitToPeers('staged-action:reverted', id)
      await queryClient.cancelQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
      const previousActions = queryClient.getQueryData<Array<StagedAction>>([STAGED_ACTIONS_QUERY_KEY])
      queryClient.setQueryData<Array<StagedAction>>(
        [STAGED_ACTIONS_QUERY_KEY],
        (old) =>
          old?.map((action) =>
            action.id === id
              ? {
                  ...action,
                  outputData: null,
                  lastInvokedAt: null,
                  resolvedAt: null,
                }
              : action,
          ) ?? [],
      )
      return { previousActions }
    },
    onSuccess: (id, _variables, context) => {
      const action = context?.previousActions?.find((a) => a.id === id)
      AmplitudeRenderer.trackEvent('Staged Action', {
        action: 'undo success',
        ...(action && stagedActionEventProperties(action)),
        actionId: id,
      })
    },
    onError: (error, id, context) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to undo action'
      const action = context?.previousActions?.find((a) => a.id === id)
      AmplitudeRenderer.trackEvent('Staged Action', {
        action: 'undo error',
        ...(action && stagedActionEventProperties(action)),
        actionId: id,
        errorMessage,
      })
      if (context?.previousActions) {
        queryClient.setQueryData([STAGED_ACTIONS_QUERY_KEY], context.previousActions)
      }
      setUndoError(id, { errorMessage })
    },
    onSettled: () => {
      setUndoingId(null)
      void queryClient
        .invalidateQueries({ queryKey: [FOR_YOU_CHATS_QUERY_KEY] })
        .catch((error) => Sentry.captureException(error))
    },
  })

  return {
    undoStagedAction: undoMutation.mutateAsync,
    isUndoing: undoMutation.isPending,
    undoingId: undoMutation.isPending ? (undoMutation.variables ?? null) : null,
  }
}

export function useArchiveStagedActions() {
  const queryClient = useQueryClient()
  const { resolveAnswerableStagedAction, archiveStagedActionBatch, unarchiveStagedActionBatch, bulkPatchMetadata } =
    useStagedActionEndpoints()
  const clearPendingDisplay = useClearPendingDisplay()
  const setArchivedDisplay = useSetAtom(archivedDisplayAtom)

  /**
   * Retiring a pending ask also has to release the run waiting on it
   * (HIG-6042). It lives here rather than on each Dismiss button so every
   * single-action archive path inherits it: the row's quick move, the Action
   * View, the thread cell, the toast's reject.
   *
   * Fire and forget, so the archive the user asked for never waits on a POST.
   * Not wired into `bulkArchiveStagedActions`: that path is the auto-clear
   * sweep and Archive All, and neither should fan out a burst of resumes.
   */
  const releaseRunFor = useCallback(
    (id: string) => {
      const cached = queryClient.getQueryData<Array<StagedAction>>([STAGED_ACTIONS_QUERY_KEY])
      const action = cached?.find((row) => row.id === id)
      releaseAnswerableRun(action, (endpoint, answer) => resolveAnswerableStagedAction(id, endpoint, answer)).catch(
        (error) => {
          Sentry.captureException(error, {
            tags: { area: 'staged-actions', operation: 'release-answerable-run' },
            extra: { actionId: id },
          })
        },
      )
    },
    [resolveAnswerableStagedAction, queryClient],
  )

  // For You membership is decided server-side by whether a conversation still
  // has a live action, so its feed refetches after every archive or restore
  // attempt — a failed one too, since the rollback also changes what it shows.
  const invalidateForYou = useCallback(
    () => queryClient.invalidateQueries({ queryKey: [FOR_YOU_CHATS_QUERY_KEY] }),
    [queryClient],
  )

  const dismissStagedAction = useCallback(
    async (id: string): Promise<void> => {
      // Before the optimistic write below, while the cache still holds the
      // card's `inputData` to build the dismissal from.
      releaseRunFor(id)
      markDeleted(id)
      const archivedAt = new Date().toISOString()
      queryClient.setQueryData<Array<StagedAction>>(
        [STAGED_ACTIONS_QUERY_KEY],
        (old) => old?.map((action) => (action.id === id ? { ...action, archivedAt } : action)) ?? [],
      )
      rememberArchiveState(id, archivedAt)
      clearPendingDisplay(id)
      if (isMockAction(id)) return
      try {
        await archiveStagedActionBatch([id])
      } catch (error) {
        console.error('[dismissStagedAction] failed:', id, error)
        forgetArchiveState(id)
        queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
      } finally {
        void invalidateForYou()
      }
    },
    [queryClient, archiveStagedActionBatch, clearPendingDisplay, invalidateForYou],
  )

  // Archive with the 10s undo window: the archive runs immediately (optimistic
  // `archivedAt` via dismissStagedAction — NOT bulkArchiveStagedActions, which
  // drops the row from the cache and with it any chance of a linger), while the
  // display marker keeps the row visible with a countdown until the window
  // elapses or Undo reverses it.
  const archiveStagedActionWithUndo = useCallback(
    async (id: string): Promise<void> => {
      const startMs = Date.now()
      setArchivedDisplay((prev) => {
        const next = new Map(prev)
        next.set(id, startMs)
        return next
      })
      // Same reasoning as `staged-action:invoked`: the synced cache write gives
      // peers the archivedAt, but the display marker is window-local — without
      // it a peer's list drops the row instantly instead of lingering.
      GlobalEvents.emitToPeers('staged-action:archived', id, startMs)
      await dismissStagedAction(id)
    },
    [dismissStagedAction, setArchivedDisplay],
  )

  // Reject an action. The terminal outcome (useful=0) is recorded server-side
  // by the archive lifecycle on the authoring span; a client-written rating
  // here would overwrite any explicit thumbs the user left on the chat turn.
  const rejectStagedAction = useCallback(
    async (id: string): Promise<void> => {
      await dismissStagedAction(id)
    },
    [dismissStagedAction],
  )

  const bulkArchiveStagedActions = useCallback(
    async (ids: Array<string>) => {
      const uniqueIds = normalizeUniqueIds(ids)
      if (uniqueIds.length === 0) return

      let previousActions: Array<StagedAction> | undefined
      try {
        uniqueIds.forEach(markDeleted)
        const uniqueIdSet = new Set(uniqueIds)
        await queryClient.cancelQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
        previousActions = queryClient.getQueryData<Array<StagedAction>>([STAGED_ACTIONS_QUERY_KEY])
        queryClient.setQueryData<Array<StagedAction>>(
          [STAGED_ACTIONS_QUERY_KEY],
          (old) => old?.filter((action) => !uniqueIdSet.has(action.id)) ?? [],
        )
        // The refetch below brings the dropped rows back. Pin them archived in
        // case it answers from before the write.
        const archivedAt = new Date().toISOString()
        uniqueIds.forEach((id) => rememberArchiveState(id, archivedAt))

        const serverIds = uniqueIds.filter((id) => !isMockAction(id))
        for (const batch of chunkIds(serverIds, 100)) {
          await archiveStagedActionBatch(batch)
        }
      } catch (error) {
        uniqueIds.forEach(forgetArchiveState)
        if (previousActions) {
          queryClient.setQueryData([STAGED_ACTIONS_QUERY_KEY], previousActions)
        }
        throw error
      } finally {
        queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
        void invalidateForYou()
      }
    },
    [queryClient, archiveStagedActionBatch, invalidateForYou],
  )

  const bulkUnarchiveStagedActions = useCallback(
    async (ids: Array<string>) => {
      const uniqueIds = normalizeUniqueIds(ids)
      if (uniqueIds.length === 0) return

      let previousActions: Array<StagedAction> | undefined
      try {
        const uniqueIdSet = new Set(uniqueIds)
        await queryClient.cancelQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
        previousActions = queryClient.getQueryData<Array<StagedAction>>([STAGED_ACTIONS_QUERY_KEY])
        queryClient.setQueryData<Array<StagedAction>>(
          [STAGED_ACTIONS_QUERY_KEY],
          (old) => old?.map((action) => (uniqueIdSet.has(action.id) ? { ...action, archivedAt: null } : action)) ?? [],
        )
        uniqueIds.forEach((id) => rememberArchiveState(id, null))

        const serverIds = uniqueIds.filter((id) => !isMockAction(id))
        for (const batch of chunkIds(serverIds, 100)) {
          await unarchiveStagedActionBatch(batch)
        }

        const statusUpdatedAt = new Date().toISOString()
        const metadataUpdates = serverIds.map((id) => ({
          id,
          metadata: { status: 'staged', statusUpdatedAt },
        }))
        for (let i = 0; i < metadataUpdates.length; i += 100) {
          await bulkPatchMetadata(metadataUpdates.slice(i, i + 100))
        }
      } catch (error) {
        uniqueIds.forEach(forgetArchiveState)
        if (previousActions) {
          queryClient.setQueryData([STAGED_ACTIONS_QUERY_KEY], previousActions)
        }
        throw error
      } finally {
        queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
        void invalidateForYou()
      }
    },
    [queryClient, unarchiveStagedActionBatch, bulkPatchMetadata, invalidateForYou],
  )

  const undoArchiveStagedAction = useCallback(
    async (id: string): Promise<void> => {
      // Unarchive first, marker second. The marker is the only thing keeping an
      // `archivedAt` row visible, so dropping it before the unarchive's
      // optimistic `archivedAt: null` write would blink the row out — and on a
      // failed unarchive (rolled back to archived) would vanish it for good,
      // the opposite of the Undo the user just pressed. Failure now leaves the
      // countdown (and another shot at Undo) in place; peers hear about the
      // revert only once it has actually happened.
      await bulkUnarchiveStagedActions([id])
      setArchivedDisplay((prev) => {
        if (!prev.has(id)) return prev
        const next = new Map(prev)
        next.delete(id)
        return next
      })
      GlobalEvents.emitToPeers('staged-action:archive-reverted', id)
    },
    [bulkUnarchiveStagedActions, setArchivedDisplay],
  )

  return {
    dismissStagedAction,
    rejectStagedAction,
    bulkArchiveStagedActions,
    bulkUnarchiveStagedActions,
    archiveStagedActionWithUndo,
    undoArchiveStagedAction,
  }
}

export function useDeleteStagedActions() {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()
  const { deleteStagedActionBatch } = useStagedActionEndpoints()

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (isMockAction(id)) return
      const token = await getAuthToken()
      const response = await fetch(getChatApiRoute(`api/v1/staged-actions/${id}`), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to delete staged action')
      }
    },
    onMutate: async (id: string) => {
      markDeleted(id)
      await queryClient.cancelQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
      const previousActions = queryClient.getQueryData<Array<StagedAction>>([STAGED_ACTIONS_QUERY_KEY])
      queryClient.setQueryData<Array<StagedAction>>(
        [STAGED_ACTIONS_QUERY_KEY],
        (old) => old?.filter((action) => action.id !== id) ?? [],
      )
      return { previousActions }
    },
    onError: (_err, _id, context) => {
      if (context?.previousActions) {
        queryClient.setQueryData([STAGED_ACTIONS_QUERY_KEY], context.previousActions)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
      // Completed actions also appear in the unified history feed.
      queryClient.invalidateQueries({ queryKey: [HISTORY_ACTIVITY_QUERY_KEY] })
    },
  })

  const bulkDeleteStagedActions = useCallback(
    async (ids: Array<string>) => {
      const uniqueIds = normalizeUniqueIds(ids)
      if (uniqueIds.length === 0) return

      let previousActions: Array<StagedAction> | undefined
      try {
        uniqueIds.forEach(markDeleted)
        const uniqueIdSet = new Set(uniqueIds)
        await queryClient.cancelQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
        previousActions = queryClient.getQueryData<Array<StagedAction>>([STAGED_ACTIONS_QUERY_KEY])
        queryClient.setQueryData<Array<StagedAction>>(
          [STAGED_ACTIONS_QUERY_KEY],
          (old) => old?.filter((action) => !uniqueIdSet.has(action.id)) ?? [],
        )

        const serverIds = uniqueIds.filter((id) => !isMockAction(id))
        for (const batch of chunkIds(serverIds, 100)) {
          await deleteStagedActionBatch(batch)
        }
      } catch (error) {
        if (previousActions) {
          queryClient.setQueryData([STAGED_ACTIONS_QUERY_KEY], previousActions)
        }
        throw error
      } finally {
        queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
        // Completed actions also appear in the unified history feed.
        queryClient.invalidateQueries({ queryKey: [HISTORY_ACTIVITY_QUERY_KEY] })
      }
    },
    [queryClient, deleteStagedActionBatch],
  )

  return {
    deleteStagedAction: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    deletingId: deleteMutation.isPending ? (deleteMutation.variables ?? null) : null,
    bulkDeleteStagedActions,
  }
}

export function useClearAllStagedActions() {
  const queryClient = useQueryClient()
  const { deleteStagedActionBatch } = useStagedActionEndpoints()
  const { bulkArchiveStagedActions } = useArchiveStagedActions()
  const { stagedActions, allActions } = useVisibleStagedActions()
  const isDeletingAllRef = useRef(false)

  const getVisibleActionIds = useCallback((): Array<string> => {
    return stagedActions.map((action) => action.id)
  }, [stagedActions])

  // Delete-all is an admin/dev control: it must clear the entire staged-actions set
  // (including archived/completed rows filtered out of the visible queue), so source
  // IDs from the full query cache rather than the visible list.
  const getAllActionIds = useCallback((): Array<string> => {
    return allActions.map((action) => action.id)
  }, [allActions])

  const deleteAllStagedActions = useCallback(async () => {
    if (isDeletingAllRef.current) return
    isDeletingAllRef.current = true
    let previousActions: Array<StagedAction> | undefined
    try {
      const uniqueIds = normalizeUniqueIds(getAllActionIds())
      if (uniqueIds.length === 0) return

      AmplitudeRenderer.trackEvent('Staged Action', {
        action: 'clear all',
        actionIds: uniqueIds,
        count: uniqueIds.length,
      })

      uniqueIds.forEach(markDeleted)
      const uniqueIdSet = new Set(uniqueIds)
      await queryClient.cancelQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
      previousActions = queryClient.getQueryData<Array<StagedAction>>([STAGED_ACTIONS_QUERY_KEY])
      queryClient.setQueryData<Array<StagedAction>>(
        [STAGED_ACTIONS_QUERY_KEY],
        (old) => old?.filter((action) => !uniqueIdSet.has(action.id)) ?? [],
      )

      const serverIds = uniqueIds.filter((id) => !isMockAction(id))
      for (const batch of chunkIds(serverIds, 100)) {
        await deleteStagedActionBatch(batch)
      }
    } catch (error) {
      console.error('Failed to delete all staged actions:', error)
      if (previousActions) {
        queryClient.setQueryData([STAGED_ACTIONS_QUERY_KEY], previousActions)
      }
      throw error
    } finally {
      isDeletingAllRef.current = false
      queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
    }
  }, [getAllActionIds, queryClient, deleteStagedActionBatch])

  const archiveAllStagedActions = useCallback(async () => {
    if (isDeletingAllRef.current) return
    isDeletingAllRef.current = true
    try {
      const uniqueIds = normalizeUniqueIds(getVisibleActionIds())
      if (uniqueIds.length === 0) return

      AmplitudeRenderer.trackEvent('Staged Action', {
        action: 'archive all',
        actionIds: uniqueIds,
        count: uniqueIds.length,
      })

      await bulkArchiveStagedActions(uniqueIds)
    } catch (error) {
      console.error('Failed to archive all staged actions:', error)
      throw error
    } finally {
      isDeletingAllRef.current = false
    }
  }, [getVisibleActionIds, bulkArchiveStagedActions])

  return { deleteAllStagedActions, archiveAllStagedActions }
}

export function useGenerateStagedActions() {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()

  const generateMutation = useMutation({
    mutationFn: async ({ audioNoteId, audioSessionId }: GenerateStagedActionsInput) => {
      AmplitudeRenderer.trackEvent('Staged Action', {
        action: 'generation requested',
        audioNoteId,
        audioSessionId,
      })
      try {
        const token = await getAuthToken()
        if (!audioNoteId && !audioSessionId) {
          throw new Error('Missing audio note or session ID for staged action generation')
        }
        const response = await fetch(getChatApiRoute('api/v1/staged-actions/generate'), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(audioSessionId ? { audioSessionId } : { audioNoteId }),
        })

        if (!response.ok) {
          throw new Error('Failed to generate staged actions')
        }

        return response.json()
      } catch (error) {
        console.error('Failed to generate staged actions:', error)
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
    },
  })

  return { generateStagedActions: generateMutation.mutateAsync, isGenerating: generateMutation.isPending }
}

export function useEditStagedAction() {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()
  const store = useStore()
  const isAgenticChatEnabled = useIsAgenticChatEnabled()

  const editStagedAction = useCallback(
    async (id: string, prompt: string): Promise<void> => {
      const updateStatus = (state: ActionEditState) => {
        store.set(stagedActionEditStatesAtom, (prev) => ({
          ...prev,
          [id]: state,
        }))
      }

      updateStatus({ status: 'pending', message: null, conversationId: null })
      const existing = queryClient
        .getQueryData<Array<StagedAction>>([STAGED_ACTIONS_QUERY_KEY])
        ?.find((a) => a.id === id)

      try {
        const token = await getAuthToken()
        const response = await fetch(getChatApiRoute(`api/v1/staged-actions/${id}/edit`), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ prompt, useAgenticChat: isAgenticChatEnabled }),
        })

        if (!response.ok) {
          const errorText = await response.text().catch(() => 'Failed to edit staged action')
          throw new Error(errorText || 'Failed to edit staged action')
        }

        const { data } = await response.json()

        if (data.kind === 'fallbackChat') {
          const message: string = data.reasoning ?? 'Edit could not be applied'
          const conversationId: string | null = data.conversationId ?? null
          updateStatus({ status: 'error', message, conversationId })
          return
        }

        const message: string | null = data.message ?? null
        const conversationId: string | null = data.conversationId ?? null
        updateStatus({ status: 'success', message, conversationId })
        // Mark edited locally so invoke metrics see wasEdited before refetch settles.
        queryClient.setQueryData<Array<StagedAction>>(
          [STAGED_ACTIONS_QUERY_KEY],
          (old) =>
            old?.map((action) =>
              action.id === id ? { ...action, metadata: { ...action.metadata, userEdited: 'true' } } : action,
            ) ?? [],
        )
        AmplitudeRenderer.trackEvent('Staged Action', {
          action: 'edit',
          ...(existing && stagedActionEventProperties(existing)),
          actionId: id,
          editMethod: 'prompt',
        })
        queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to edit staged action'
        updateStatus({ status: 'error', message, conversationId: null })
      }
    },
    [getAuthToken, queryClient, store, isAgenticChatEnabled],
  )

  return { editStagedAction }
}

export function useStagedActionHooks() {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()

  const deleteHook = useCallback(
    async (id: string, trigger: string, effectType: string): Promise<void> => {
      const previousActions = queryClient.getQueryData<Array<StagedAction>>([STAGED_ACTIONS_QUERY_KEY])
      const target = previousActions?.find((a) => a.id === id)
      const nextHooks = target?.hooks?.filter((h) => !(h.trigger === trigger && h.effect?.type === effectType)) ?? []

      queryClient.setQueryData<Array<StagedAction>>(
        [STAGED_ACTIONS_QUERY_KEY],
        (old) => old?.map((action) => (action.id === id ? { ...action, hooks: nextHooks } : action)) ?? [],
      )

      if (isMockAction(id)) return
      try {
        const token = await getAuthToken()
        const response = await fetch(getChatApiRoute(`api/v1/staged-actions/${id}`), {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ hooks: nextHooks }),
        })
        if (!response.ok) {
          throw new Error('Failed to update staged action hooks')
        }
      } catch (error) {
        console.error('[deleteHook] failed:', id, trigger, effectType, error)
        if (previousActions) {
          queryClient.setQueryData([STAGED_ACTIONS_QUERY_KEY], previousActions)
        }
        queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
      }
    },
    [queryClient, getAuthToken],
  )

  return { deleteHook }
}

/** Writes metadata into the cache at once and to the server in the next batch. */
function useUpdateActionMetadataBatched(): (id: string, updates: Record<string, string>) => void {
  const queryClient = useQueryClient()
  const { bulkPatchMetadata } = useStagedActionEndpoints()

  useMetadataFlush(bulkPatchMetadata)

  return useCallback(
    (id: string, updates: Record<string, string>): void => {
      queryClient.setQueryData<Array<StagedAction>>(
        [STAGED_ACTIONS_QUERY_KEY],
        (old) =>
          old?.map((action) =>
            action.id === id ? { ...action, metadata: { ...action.metadata, ...updates } } : action,
          ) ?? [],
      )
      if (isMockAction(id)) return
      schedulePendingMetadata(id, updates)
    },
    [queryClient],
  )
}

/** Pins or unpins actions, as a chat's to-do pins: see `isPinnedStagedAction`. */
export function useSetStagedActionsPinned(): (ids: Array<string>, pinned: boolean) => void {
  const updateActionMetadataBatched = useUpdateActionMetadataBatched()

  return useCallback(
    (ids: Array<string>, pinned: boolean) => {
      const pinnedAt = pinned ? new Date().toISOString() : ''
      for (const id of ids) updateActionMetadataBatched(id, { pinnedAt })
    },
    [updateActionMetadataBatched],
  )
}

export function useStagedActionReadState() {
  const setManuallyHidden = useSetAtom(manuallyHiddenAtom)
  const clearPendingDisplay = useClearPendingDisplay()
  const updateActionMetadataBatched = useUpdateActionMetadataBatched()

  const markActionAsRead = useCallback(
    (id: string, currentAction?: StagedAction) => {
      if (currentAction?.metadata?.readAt) return
      updateActionMetadataBatched(id, { readAt: new Date().toISOString() })
    },
    [updateActionMetadataBatched],
  )

  const markActionAsUnread = useCallback(
    (id: string) => {
      updateActionMetadataBatched(id, { readAt: '' })
    },
    [updateActionMetadataBatched],
  )

  const markActionExpired = useCallback(
    (id: string) => {
      setManuallyHidden((prev) => {
        if (prev.has(id)) return prev
        const next = new Set(prev)
        next.add(id)
        while (next.size > MAX_MANUALLY_HIDDEN_ACTIONS) {
          const oldest = next.values().next().value
          if (!oldest) break
          next.delete(oldest)
        }
        return next
      })
      clearPendingDisplay(id)
    },
    [setManuallyHidden, clearPendingDisplay],
  )

  return { markActionAsRead, markActionAsUnread, markActionExpired }
}

export function usePruneHiddenStagedActions(allActions: Array<StagedAction>, hasFetched: boolean): void {
  const setManuallyHidden = useSetAtom(manuallyHiddenAtom)

  useEffect(() => {
    if (!hasFetched) return
    const currentIds = new Set(allActions.map((action) => action.id))
    setManuallyHidden((prev) => {
      if (prev.size === 0) return prev
      const next = new Set<string>()
      for (const id of prev) {
        if (currentIds.has(id)) next.add(id)
      }
      return next.size === prev.size ? prev : next
    })
  }, [allActions, hasFetched, setManuallyHidden])
}

export function useStagedActionsAdmin() {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()
  const { bulkPatchMetadata } = useStagedActionEndpoints()
  const setInvokeErrors = useSetAtom(invokeErrorsAtom)
  const setUndoErrors = useSetAtom(undoErrorsAtom)
  const setPendingDisplay = useSetAtom(pendingDisplayAtom)
  const setManuallyHidden = useSetAtom(manuallyHiddenAtom)

  const createMutation = useMutation({
    mutationFn: async (input: CreateStagedActionInput) => {
      const token = await getAuthToken()
      const response = await fetch(getChatApiRoute('api/v1/staged-actions'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      })

      if (!response.ok) {
        throw new Error('Failed to create staged action')
      }

      const json = await response.json()
      return json.data as StagedAction
    },
    onSuccess: (created) => {
      // Optimistically upsert the created action (the POST response is the full
      // server row) rather than refetching. Cross-window cache sync propagates it.
      queryClient.setQueryData<Array<StagedAction>>([STAGED_ACTIONS_QUERY_KEY], (prev = []) => [
        ...prev.filter((a) => a.id !== created.id),
        created,
      ])
    },
  })

  const invalidateStagedActions = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
  }, [queryClient])

  const rerenderStagedActions = useCallback(async (): Promise<void> => {
    try {
      const token = await getAuthToken()
      const response = await fetch(getChatApiRoute('api/v1/staged-actions/test-recalculate-view'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Failed to rerender staged actions')
        throw new Error(errorText || 'Failed to rerender staged actions')
      }

      await queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
    } catch (error) {
      console.error('Failed to rerender staged actions:', error)
      throw error
    }
  }, [getAuthToken, queryClient])

  const resetAllLocalStates = useCallback(async () => {
    setInvokeErrors(new Map())
    setUndoErrors(new Map())
    setPendingDisplay(new Map())
    setManuallyHidden(new Set())
    clearExecutionMarks()
    clearArchiveMarks()

    const actions = queryClient.getQueryData<Array<StagedAction>>([STAGED_ACTIONS_QUERY_KEY]) ?? []
    const updates = actions
      .filter((a) => !isMockAction(a.id))
      .map((action) => ({
        id: action.id,
        metadata: { readAt: '' },
      }))
    queryClient.setQueryData<Array<StagedAction>>(
      [STAGED_ACTIONS_QUERY_KEY],
      (old) =>
        old?.map((a) => ({
          ...a,
          metadata: { ...a.metadata, readAt: '' },
        })) ?? [],
    )
    if (updates.length > 0) {
      await bulkPatchMetadata(updates)
    }
  }, [queryClient, bulkPatchMetadata, setInvokeErrors, setUndoErrors, setPendingDisplay, setManuallyHidden])

  return {
    createStagedAction: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    invalidateStagedActions,
    rerenderStagedActions,
    resetAllLocalStates,
  }
}
